// Import interfaces.
import {
	IAddress
} from '@app/interfaces';

export default class AddressService {

	private static instance: AddressService;

	private constructor() {

	}

	/**
	* Get a singleton instance of AddressService.
	*/
	public static getInstance(): AddressService {
		if (!AddressService.instance) {
			AddressService.instance = new AddressService();
		}
		return AddressService.instance;
	}

	public doAddressesMatch(address1: IAddress, address2: IAddress): boolean {
		return !this.isAddressEmpty(address1)
			&& !this.isAddressEmpty(address2)
			&& address1.addressLine1?.toLowerCase() === address2.addressLine1?.toLowerCase()
			&& address1.addressLine2?.toLowerCase() === address2.addressLine2?.toLowerCase()
			&& address1.city?.toLowerCase() === address2.city?.toLowerCase()
			&& address1.state?.toLowerCase() === address2.state?.toLowerCase()
			&& address1.postalCode?.toLowerCase() === address2.postalCode?.toLowerCase()
	}

	public isAddressNullOrEmpty(address: IAddress | undefined | null): boolean {
		return address === null
			|| address === undefined
			|| this.isAddressEmpty(address);
	}

	public isAddressEmpty(address: IAddress): boolean {
		return String.isNullOrWhiteSpace(address.addressLine1)
			&& String.isNullOrWhiteSpace(address.addressLine2)
			&& String.isNullOrWhiteSpace(address.city)
			&& String.isNullOrWhiteSpace(address.state)
			&& String.isNullOrWhiteSpace(address.postalCode);
	}

	public isAddressMissingInformation(address: IAddress): boolean {
		return String.isNullOrWhiteSpace(address.addressLine1)
			|| String.isNullOrWhiteSpace(address.city)
			|| String.isNullOrWhiteSpace(address.state)
			|| String.isNullOrWhiteSpace(address.postalCode);
	}

	/**
	 * Formats an address instance to a single line address with title casing.
	 * @param address
	 */
	public formatAddressToSingleLineStringWithTitleCasing(address: IAddress): string {
		return this.formatAddressToSingleLineStringWithComponents(address.addressLine1?.toTitleCase(), address.addressLine2?.toTitleCase(), address.city?.toTitleCase(), address.state, address.postalCode);
	}

	/**
	 * Formats an address instance to a single line address.
	 * @param address
	 */
	public formatAddressToSingleLineString(address: IAddress): string {
		return this.formatAddressToSingleLineStringWithComponents(address.addressLine1, address.addressLine2, address.city, address.state, address.postalCode);
	}

	/**
	 * Formats the address components to a single line address.
	 * @param addressLine1
	 * @param addressLine2
	 * @param city
	 * @param state
	 * @param postalCode
	 */
	public formatAddressToSingleLineStringWithComponents(addressLine1: string | null | undefined, addressLine2: string | null | undefined, city: string | null | undefined, state: string | null | undefined, postalCode: string | null | undefined): string {
		let addressLine: string = this.formatStreetAddressToSingleLineStringWithComponents(addressLine1, addressLine2);

		let completeAddress: string = [addressLine, city, state, postalCode]
			.filter((addressToken: string | null | undefined) => addressToken !== undefined && addressToken !== null && addressToken !== String.empty())
			.join(", ");

		return completeAddress;
	}

	/**
	 * Formats the street address from an address instance with title casing.
	 * @param address
	 */
	public formatStreetAddressToSingleLineStringWithTitleCasing(address: IAddress): string {
		return this.formatStreetAddressToSingleLineStringWithComponents(address.addressLine1?.toTitleCase(), address.addressLine2?.toTitleCase());
	}

	/**
	 * Formats the street address from an address instance.
	 * @param address
	 */
	public formatStreetAddressToSingleLineString(address: IAddress): string {
		return this.formatStreetAddressToSingleLineStringWithComponents(address.addressLine1, address.addressLine2);
	}

	/**
	 * Formats the street address to a single line string using address components.
	 * @param addressLine1
	 * @param addressLine2
	 */
	public formatStreetAddressToSingleLineStringWithComponents(addressLine1: string | null | undefined, addressLine2: string | null | undefined): string {
		let addressLine: string = [addressLine1, addressLine2]
			.filter((addressToken: string | null | undefined) => addressToken !== undefined && addressToken !== null && addressToken !== String.empty())
			.join(" ");

		return addressLine;
	}

	/**
	 * Formats the street address from an address instance with title casing.
	 * @param address
	 */
	public formatCityStateToSingleLineStringWithTitleCasing(address: IAddress): string {
		return this.formatCityStateToSingleLineStringWithComponents(address.city?.toTitleCase(), address.state);
	}

	/**
	 * Formats the street address from an address instance.
	 * @param address
	 */
	public formatCityStateToSingleLineString(address: IAddress): string {
		return this.formatCityStateToSingleLineStringWithComponents(address.city, address.state);
	}

	/**
	 * Formats the street address to a single line string using address components.
	 * @param addressLine1
	 * @param addressLine2
	 */
	public formatCityStateToSingleLineStringWithComponents(city: string | null | undefined, state: string | null | undefined): string {
		let addressLine: string = [city, state]
			.filter((addressToken: string | null | undefined) => addressToken !== undefined && addressToken !== null && addressToken !== String.empty())
			.join(", ");

		return addressLine;
	}

	/**
	 * Checks to see if the input value follows the zip format.
	 * @param value
	 */ 
	public isStringZipFormat(value: string): boolean {
		return new RegExp(/^(\d{5})(-\d{4})?$/).test(value);
	}
}